import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Handshake, CheckCircle2, MapPin } from "lucide-react";
import { useLang } from "../../lib/i18n";
import { Reveal } from "../motion/Reveal";
import { SectionKicker } from "./Manifesto";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { DISTRIBUTOR_COUNTRIES, WEST_AFRICA_COUNTRIES } from "../../lib/data";
import { AfricaDistributorMap } from "./AfricaDistributorMap";
import { DistributorDirectory } from "./DistributorDirectory";

export const DistributorSection = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", country: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Map "Apply to distribute here" → prefill country + scroll the form into view.
  useEffect(() => {
    const onSelect = (e) => {
      const country = e.detail;
      setForm((f) => ({ ...f, country }));
      document.getElementById("distributor-apply")?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    window.addEventListener("select-distributor-country", onSelect);
    return () => window.removeEventListener("select-distributor-country", onSelect);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.country) {
      toast.error(t.distributor.countryPlaceholder);
      return;
    }
    setLoading(true);
    try {
      await api.post("/distributors", form);
      setDone(true);
      window.dispatchEvent(new Event("distributor-applied"));
      toast.success(t.distributor.success);
    } catch (err) {
      toast.error(t.distributor.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="distributors" className="relative z-10 py-24 md:py-32 border-t border-white/10 bg-[#161616]" data-testid="distributor-section">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        {/* Copy */}
        <Reveal>
          <SectionKicker>{t.distributor.kicker}</SectionKicker>
          <h2 className="font-display font-extrabold tracking-tighter text-3xl md:text-5xl leading-[0.95]">
            {t.distributor.title}
          </h2>
          <p className="mt-5 text-white/60 max-w-lg">{t.distributor.sub}</p>

          <p className="mt-8 text-[11px] uppercase tracking-[0.2em] text-white/40">{t.distributor.countriesLabel}</p>
          <div className="mt-3 flex flex-wrap gap-2.5" data-testid="distributor-countries">
            {DISTRIBUTOR_COUNTRIES.map((c) => (
              <span
                key={c}
                data-testid={`distributor-country-${c}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-lime2/30 bg-lime2/5 px-3.5 py-1.5 text-sm text-lime2"
              >
                <MapPin size={13} /> {c}
              </span>
            ))}
          </div>

          <div className="mt-8">
            <AfricaDistributorMap />
          </div>
        </Reveal>

        {/* Form */}
        <Reveal delay={0.1}>
          <div id="distributor-apply" className="rounded-2xl border border-white/10 bg-[#181818] p-7 md:p-10 scroll-mt-24">
            {done ? (
              <div className="flex flex-col items-center text-center py-10" data-testid="distributor-success">
                <CheckCircle2 size={56} className="text-lime2" />
                <p className="mt-5 font-display text-2xl font-bold">{t.distributor.success}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="distributor-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input required value={form.name} onChange={set("name")} placeholder={t.distributor.name} data-testid="distributor-name" className="bg-charcoal border-white/15 h-12" />
                  <Input required value={form.company} onChange={set("company")} placeholder={t.distributor.company} data-testid="distributor-company" className="bg-charcoal border-white/15 h-12" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input required type="email" value={form.email} onChange={set("email")} placeholder={t.distributor.email} data-testid="distributor-email" className="bg-charcoal border-white/15 h-12" />
                  <Input required value={form.phone} onChange={set("phone")} placeholder={t.distributor.phone} data-testid="distributor-phone" className="bg-charcoal border-white/15 h-12" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                    <SelectTrigger data-testid="distributor-country" className="bg-charcoal border-white/15 h-12 text-base">
                      <SelectValue placeholder={t.distributor.countryPlaceholder} />
                    </SelectTrigger>
                    <SelectContent>
                      {DISTRIBUTOR_COUNTRIES.map((c) => (
                        <SelectItem key={c} value={c} data-testid={`distributor-country-opt-${c}`}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input value={form.city} onChange={set("city")} placeholder={t.distributor.city} data-testid="distributor-city" className="bg-charcoal border-white/15 h-12" />
                </div>
                <Textarea value={form.message} onChange={set("message")} placeholder={t.distributor.message} data-testid="distributor-message" className="bg-charcoal border-white/15 min-h-[90px]" />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="distributor-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald2 hover:bg-lime2 hover:text-black text-white font-semibold rounded-full py-4 transition-colors duration-300 disabled:opacity-60"
                >
                  <Handshake size={16} /> {loading ? t.distributor.submitting : t.distributor.submit}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>

      {/* Dedicated West-Africa regional spotlight map */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-16 md:mt-24">
        <Reveal>
          <div className="max-w-2xl">
            <SectionKicker>{t.distributor.regionTitle}</SectionKicker>
            <p className="mt-4 text-white/60">{t.distributor.regionSub}</p>
          </div>
          <div className="mt-8">
            <AfricaDistributorMap
              countries={WEST_AFRICA_COUNTRIES}
              projectionConfig={{ scale: 1500, center: [1.5, 12.6] }}
              hintText={t.distributor.regionHint}
              testId="westafrica-map"
            />
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <DistributorDirectory />
        </Reveal>
      </div>
    </section>
  );
};
